import { useRef, useCallback } from 'react';
import { Camera, Loader2, Check, Minus, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { useProfilePhoto } from '@/features/profile/hooks/useProfilePhoto';
import { useToast } from '@/shared/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { BELT_ORDER, type BeltColor } from '@/constants/belt';
import { cn } from '@/lib/utils';

// ── Belt visuals (colors only; labels/descriptions come from i18n) ─────────────

const BELT_COLORS: Record<BeltColor, string> = {
  white:  '#f5f5f5',
  blue:   '#3b82f6',
  purple: '#8e44ad',
  brown:  '#7b3f00',
  black:  '#111827',
};

export const MAX_DEGREES = 4;

interface AcademyOption {
  id: string;
  name: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Returns up to 2 initials from the typed name or username fallback. */
const getInitials = (name: string, username: string): string => {
  const trimmed = name.trim();
  if (!trimmed) return (username?.[0] ?? '?').toUpperCase();
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return words[0].slice(0, 2).toUpperCase();
};

/** Horizontal belt bar with a stripe zone reflecting the current degrees. */
const BeltVisual = ({ belt, stripes }: { belt: BeltColor; stripes: number }) => (
  <div
    className={cn(
      'relative h-6 w-24 flex-shrink-0 rounded',
      (belt === 'white' || belt === 'black') && 'border border-white/15'
    )}
    style={{ backgroundColor: BELT_COLORS[belt] }}
  >
    <div className="absolute right-3 top-0 bottom-0 flex w-7 items-center justify-center gap-[3px] rounded-sm bg-black/80">
      {Array.from({ length: stripes }).map((_, i) => (
        <span key={i} className="h-3 w-[3px] rounded-full bg-white" />
      ))}
    </div>
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────

interface OnboardingStep1Props {
  username: string;
  name: string;
  belt: BeltColor;
  degrees: number;
  photoUrl: string | null;
  academyId: string;
  academyName: string;
  onNameChange: (name: string) => void;
  onBeltChange: (belt: BeltColor) => void;
  onDegreesChange: (degrees: number) => void;
  onPhotoChange: (url: string) => void;
  onAcademyChange: (id: string, name: string) => void;
}

export const OnboardingStep1Profile = ({
  username,
  name,
  belt,
  degrees,
  photoUrl,
  academyId,
  academyName,
  onNameChange,
  onBeltChange,
  onDegreesChange,
  onPhotoChange,
  onAcademyChange,
}: OnboardingStep1Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { t } = useI18n();
  const { state, uploadPhoto } = useProfilePhoto();

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await uploadPhoto(file);
    if (result.success && result.url) {
      onPhotoChange(result.url);
    } else {
      toast({ title: t('onboarding.error'), description: result.error || t('onboarding.profile_update_error'), variant: 'destructive' });
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [uploadPhoto, onPhotoChange, toast, t]);

  const initials = getInitials(name, username);
  const academyValue = academyId ? { id: academyId, name: academyName } : '';

  return (
    <div className="space-y-6">

      {/* ── Avatar (horizontal) ─────────────────────────────────────── */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={state.isUploading}
          className="relative flex-shrink-0 focus:outline-none"
          aria-label={t('onboarding.profile_step.add_photo')}
        >
          {state.isUploading && (
            <div className="absolute inset-0 z-30 flex items-center justify-center rounded-full bg-black/50" style={{ width: 80, height: 88 }}>
              <Loader2 className="h-5 w-5 animate-spin text-white" />
            </div>
          )}
          <FramedAvatar
            src={photoUrl ?? undefined}
            initials={initials}
            beltRank={belt}
            frameSize={{ w: 80, h: 88 }}
            size={54}
            isManual
          />
          <span className="pointer-events-none absolute bottom-0 right-0 z-20 flex h-6 w-6 items-center justify-center rounded-full border-2 border-sossiety-dark bg-sossiety-red text-white shadow-md">
            <Camera className="h-3 w-3" />
          </span>
        </button>
        <div>
          <p className="text-[15px] font-semibold text-white">{t('onboarding.profile_step.add_photo')}</p>
          <p className="text-xs text-white/40">{t('onboarding.profile_step.photo_hint')}</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* ── Name field ──────────────────────────────────────────────── */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-white/70">{t('onboarding.profile_step.name_label')}</label>
        <Input
          type="text"
          placeholder={t('onboarding.profile_step.name_placeholder')}
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          showFloatingLabel={false}
        />
      </div>

      {/* ── Belt selector (vertical rows) ───────────────────────────── */}
      <div className="space-y-2.5">
        <label className="text-xs font-medium text-white/70">{t('onboarding.profile_step.belt_label')}</label>
        <div className="space-y-2.5">
          {BELT_ORDER.map((b) => {
            const isSelected = belt === b;
            return (
              <button
                key={b}
                type="button"
                onClick={() => onBeltChange(b)}
                className={cn(
                  'flex w-full items-center gap-4 rounded-2xl border p-3.5 text-left transition-all',
                  isSelected
                    ? 'border-sossiety-red bg-sossiety-red-surface'
                    : 'border-white/10 bg-[#231a1a] hover:border-white/20'
                )}
              >
                <BeltVisual belt={b} stripes={isSelected ? degrees : 0} />
                <div className="flex-1">
                  <p className="text-[15px] font-bold text-white">{t(`onboarding.profile_step.belts.${b}.name`)}</p>
                  <p className="text-xs text-white/40">{t(`onboarding.profile_step.belts.${b}.description`)}</p>
                </div>
                <span
                  className={cn(
                    'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors',
                    isSelected ? 'border-sossiety-red bg-sossiety-red' : 'border-white/20'
                  )}
                >
                  {isSelected && <Check className="h-3 w-3 text-white" />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Degrees stepper ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#231a1a] p-4">
        <div>
          <p className="text-sm font-semibold text-white">{t('onboarding.profile_step.degrees_label')}</p>
          <p className="text-xs text-white/40">{t('onboarding.profile_step.degrees_hint')}</p>
          <div className="mt-2 flex gap-1.5">
            {Array.from({ length: MAX_DEGREES }).map((_, i) => (
              <span key={i} className={cn('h-2 w-2 rounded-full', i < degrees ? 'bg-sossiety-red' : 'bg-white/15')} />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onDegreesChange(Math.max(0, degrees - 1))}
            disabled={degrees <= 0}
            aria-label="-1"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 bg-sossiety-dark text-white transition-colors hover:border-sossiety-red disabled:opacity-40"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="w-6 text-center text-xl font-extrabold text-white">{degrees}</span>
          <button
            type="button"
            onClick={() => onDegreesChange(Math.min(MAX_DEGREES, degrees + 1))}
            disabled={degrees >= MAX_DEGREES}
            aria-label="+1"
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/15 bg-sossiety-dark text-white transition-colors hover:border-sossiety-red disabled:opacity-40"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* ── Academy / Team ──────────────────────────────────────────── */}
      <div className="space-y-1">
        <label className="text-xs font-medium text-white/70">{t('onboarding.profile_step.academy_label')}</label>
        <AcademyAutocomplete
          value={academyValue}
          onChange={(academy) => {
            if (academy && typeof academy === 'object') {
              onAcademyChange((academy as AcademyOption).id, (academy as AcademyOption).name);
            } else {
              onAcademyChange('', (academy as string) || '');
            }
          }}
          placeholder={t('onboarding.profile_step.academy_placeholder')}
          theme="dark"
        />
      </div>

    </div>
  );
};
